// LooksEditPostForm.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { IoMdCloudUpload, IoMdImages } from 'react-icons/io';
import heic2any from 'heic2any';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { FaInstagram, FaTwitter, FaTiktok, FaYoutube, FaFacebook, FaSoundcloud } from 'react-icons/fa';
import CloseButton from './CloseButton';
import { uploadImageToStorage, updatePostSummary } from '../utils/dbUtilities';
import './PostForm.css';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

/* ── Ice-Breaker prompts (add just below the imports) ───────────── */
export const ICE_BREAKER_QUESTIONS = [
  { id: 'origin',             label: 'Where are you from?',                                             type: 'text'  },
  { id: 'favoriteFood',       label: "What's your favorite food?",                                      type: 'text'  },
  { id: 'favCountryVisit',    label: 'Favorite country you’ve visited?',                                type: 'text'  },
  { id: 'musicGenre',         label: 'Which song or artist have you been playing on repeat lately?',   type: 'text'  },
  { id: 'dreamMeet',          label: 'If you could meet anyone (dead or alive), who would it be?',     type: 'text'  },
  { id: 'favDessert',         label: "What's your favorite dessert?",                                   type: 'text'  },

  /* last prompt → two-option checkbox question */
  {
    id: 'millionOrBillion',
    label: 'Would you take $1 million guaranteed or flip a coin for $1 billion?',
    type: 'choice',
    options: ['Take the $1 million', 'Flip the coin']
  }
];

// ========== Constants & Utilities ==========
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const invalidFileTypes = ['video/mp4', 'audio/wav', 'image/gif', 'video/x-ms-wmv'];

const checkFileValidity = (file, setError, setProcessing, setProgress) => {
  if (invalidFileTypes.includes(file.type)) {
    const errorMsg = 'Invalid file type. MP4, WAV, GIF, and WMV files are not allowed.';
    setError(errorMsg);
    setProcessing(false);
    setProgress(0);
    return false;
  }

  if (file.size > MAX_FILE_SIZE) {
    const errorMsg = 'File size exceeds 5 MB limit.';
    setError(errorMsg);
    setProcessing(false);
    setProgress(0);
    return false;
  }

  setError('');
  return true;
};

const compressImage = (file, setProgress) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, img.width, img.height);
      canvas.toBlob(
        (blob) => {
          setProgress(70);
          resolve(blob);
        },
        'image/jpeg',
        0.5
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const convertImageToJPG = async (file, setProgress) => {
  if (file.type === 'image/heic') {
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.92
      });
      setProgress(50);
      return convertedBlob;
    } catch (error) {
      console.error('Error converting image:', error);
      throw error;
    }
  } else {
    return compressImage(file, setProgress);
  }
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, circular = false) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  // Center image on canvas
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
    0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y
  );

  if (circular) {
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      canvas.width / 2,
      0,
      2 * Math.PI,
      true
    );
    ctx.fill();
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg');
  });
};

/* =========================================================================
   PROFILE-IMAGE DROPZONE (dual-blob: circle + square) – full component
   ========================================================================= */
function ProfileImageDropzone({
  onImageUpload,
  setIsImageProcessing,
  setProgress,
  setUploadError,
  initialImage,
  onCropperOpen
}) {
  const [selectedImage,       setSelectedImage]       = useState(null);
  const [originalImage,       setOriginalImage]       = useState(null);
  const [croppedImagePreview, setCroppedImagePreview] = useState(initialImage || null);
  const [crop,  setCrop]  = useState({ x: 0, y: 0 });
  const [zoom,  setZoom]  = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  /* keep preview in-sync if parent passes a new initial image */
  useEffect(() => {
    setCroppedImagePreview(initialImage || null);
  }, [initialImage]);

  /* tell parent when the cropper (full-screen editor) is open */
  useEffect(() => {
    onCropperOpen(!!selectedImage && !croppedImagePreview);
  }, [selectedImage, croppedImagePreview, onCropperOpen]);

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  /* ---------- SAVE ---------- */
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const circleBlob = await getCroppedImg(selectedImage, croppedAreaPixels, true);
      const resp       = await fetch(selectedImage);
      const squareBlob = await resp.blob();                       // original aspect

      setCroppedImagePreview(URL.createObjectURL(circleBlob));
      setSelectedImage(null);

      /* send BOTH blobs up */
      onImageUpload({ circle: circleBlob, square: squareBlob });
    } catch (err) {
      console.error('Error cropping image:', err);
    }
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setCroppedImagePreview(initialImage || null);
    setOriginalImage(null);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    setSelectedImage(croppedImagePreview);
    setCroppedImagePreview(null);
  };

  const handleRemove = () => {
    setSelectedImage(null);
    setCroppedImagePreview(null);
    setOriginalImage(null);
    onImageUpload(null);
  };

  const onDrop = async (acceptedFiles) => {
    setIsImageProcessing(true);
    setProgress(5);

    const file = acceptedFiles[0];
    if (!checkFileValidity(file, setUploadError, setIsImageProcessing, setProgress)) return;

    const imageBlob =
      file.type === 'image/heic'
        ? await convertImageToJPG(file, setProgress)
        : await compressImage(file, setProgress);

    const url = URL.createObjectURL(imageBlob);
    setOriginalImage(url);
    setSelectedImage(url);
    setCroppedImagePreview(null);
    setProgress(80);
  };

  const { getRootProps, getInputProps } = useDropzone({ accept: 'image/*', onDrop });

  /* ---------- RENDER ---------- */
  return (
    <div className="image-upload-section profile-image">
      {!selectedImage && !croppedImagePreview && (
        <div {...getRootProps()} className="drag-drop-zone circular">
          <input {...getInputProps()} />
          <IoMdCloudUpload size={50} className="upload-icon" />
          <p>Upload Profile Image&nbsp;(5 MB&nbsp;limit)</p>
          <IoMdImages size={50} className="images-icon" />
        </div>
      )}

      {croppedImagePreview && (
        <div className="image-preview-container circular">
          <img
            src={croppedImagePreview}
            alt="Cropped avatar"
            className="uploaded-image-preview"
          />
          <button onClick={handleRemove} className="remove-image-icon">×</button>
          <button onClick={handleEdit}   className="edit-button">Edit</button>
        </div>
      )}

      {selectedImage && !croppedImagePreview && (
        <div className="cropper-wrapper circular">
          <div className="cropper-container circular">
            <Cropper
              image={selectedImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropShape="round"
              showGrid={false}
              style={{
                containerStyle: { width: '100%', height: '300px', position: 'relative' },
                cropAreaStyle:  { border: '2px solid white', borderRadius: '50%' },
                mediaStyle:     { transform: 'translateZ(0)' }
              }}
            />
          </div>
          <div className="cropper-toolbar">
            <button type="button" onClick={handleSave}   className="save-button">Save</button>
            <button type="button" onClick={handleCancel} className="cancel-button">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ========== LooksEditPostForm Component ==========
const LooksEditPostForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading } = useAuth();
  const { updateSinglePost, updatePostInDB } = useContext(LooksPostsContext);

  // Post state
  const [post, setPost] = useState(null);

  // Image states
  const [profileImageBlob, setProfileImageBlob] = useState(null);
  const [profileImageSquareBlob, setProfileImageSquareBlob] = useState(null); // NEW – full frame
  const [profileImageProcessing, setProfileImageProcessing] = useState(false);
  const [profileImageProgress, setProfileImageProgress] = useState(0);
  const [profileImageError, setProfileImageError] = useState('');

  // Cropper open states
  const [isProfileCropperOpen, setIsProfileCropperOpen] = useState(false);

  // Form fields
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [country, setCountry] = useState('');
  const [userLocation, setUserLocation] = useState('');
  const [link, setLink] = useState('');
  const [instagram, setInstagram] = useState('');
  const [twitter, setTwitter] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [youtube, setYoutube] = useState('');
  const [facebook, setFacebook] = useState('');
  const [soundcloud, setSoundcloud] = useState('');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState(''); // optional

  /* ---- Ice-breaker answers -------- */
  const [iceBreakers, setIceBreakers] = useState(() =>
    ICE_BREAKER_QUESTIONS.reduce((acc, q) => {
      acc[q.id] = '';
      return acc;
    }, {})
  );

  // Word counts
  const [wordCount, setWordCount] = useState(0);

  // Goals
  const [goals, setGoals] = useState([]);

    // Validation
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
  
    // Post images
    const [profileImagePreview, setProfileImagePreview] = useState(null);
  
    // NEW top-level error for missing/invalid fields
    const [generalError, setGeneralError] = useState('');
  
    // Goals array
    const sortedGoals = [
      'Introduce Myself',
      'Grow My Audience',
      'Increase My Following Across Socials',
      'Increase My Online Presence',
      'Build Awareness',
      'Expand My Network', 
      'Reach New People',      
      'Get Constructive Feedback',
      'Have Fun!'
    ];
  
    // 1) Wait for Auth
    useEffect(() => {
      if (!loading && !currentUser) {
        navigate('/login');
      }
    }, [loading, currentUser, navigate]);
  
    // 2) Scroll top
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  
    // Word Count helpers
    const updateWordCount = (text) => {
      const words = text.split(/\s+/).filter((w) => w.length > 0);
      setWordCount(words.length);
    };
  
    // 3) Fetch Post
    useEffect(() => {
      const fetchPost = async () => {
        try {
          const docRef  = doc(db, 'looksPosts', id);
          const docSnap = await getDoc(docRef);
    
          if (!docSnap.exists()) {
            console.log('No such document!');
            navigate('/');
            return;
          }
    
          const postData = docSnap.data();
    
          // Security check – only the author may edit
          if (postData.userId !== currentUser.uid) {
            console.log('Not authorized to edit');
            navigate('/');
            return;
          }
    
          /* ─── store post & pre-fill the form ─── */
          setPost(postData);
    
          setDescription(postData.description || '');
          setCity(postData.city || '');
          setStateVal(postData.state || '');
          setCountry(postData.country || '');
          setUserLocation(postData.location || '');
          setLink(postData.link || '');
          setInstagram(postData.instagram || '');
          setTwitter(postData.twitter || '');
          setTiktok(postData.tiktok || '');
          setYoutube(postData.youtube || '');
          setFacebook(postData.facebook || '');
          setGoals(postData.goals || []);
          setIceBreakers(postData.iceBreakers || {});
          setEmail(postData.email || '');
          setUserName(postData.userName || '');
    
          /* 🔄  choose the *avatar* for on-screen preview first */
          setProfileImagePreview(
            postData.profileImageFullUrl      // master square
            || postData.profileImageUrl       // avatar
            || null
          );
    
          updateWordCount(postData.description || '');
        } catch (err) {
          console.error('Error fetching post:', err);
          navigate('/');
        }
      };
    
      if (!loading && currentUser?.uid) fetchPost();
    }, [id, loading, currentUser, navigate]);
  
    // 4) Possibly load the most recent bio
    useEffect(() => {
  const loadMostRecentBio = async () => {
    try {
      const q = query(
        collection(db, 'looksPosts'),
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) return;

      const last = snap.docs[0].data();

      if (last.userName) setUserName(last.userName);

      setProfileImagePreview(
        last.profileImageFullUrl          // square first
        || last.profileImageUrl
        || null
      );
    } catch (err) {
      console.error('Error loading most-recent bio:', err);
    }
  };

  if (post && currentUser) loadMostRecentBio();
}, [post, currentUser]);
  
    // 6) Possibly load from localStorage
    useEffect(() => {
      const savedUserName = localStorage.getItem('userName');
      const savedProfileImageUrl = localStorage.getItem('profileImageUrl');
  
      if (post && !post.userName && savedUserName) {
        setUserName(savedUserName);
      }
      if (post && !post.profileImageUrl && savedProfileImageUrl) {
        setProfileImagePreview(savedProfileImageUrl);
      }
    }, [post]);
  
    // Goals in localStorage
    useEffect(() => {
      const storedGoals = JSON.parse(localStorage.getItem('editGoals')) || [];
      if (!goals.length && storedGoals.length) {
        setGoals(storedGoals);
      }
    }, []);
    useEffect(() => {
      localStorage.setItem('editGoals', JSON.stringify(goals));
    }, [goals]);
  
    // Profile image
    const handleProfileImageUpload = ({ circle, square }) => {
      /* ① keep the circular avatar blob (small chip / feeds) */
      setProfileImageBlob(circle);

      /* ② keep the full-frame square blob (used by zoom modal) */
      setProfileImageSquareBlob(square);

      /* ③ local preview uses the avatar – fall back to nothing */
      if (circle) {
        setProfileImagePreview(URL.createObjectURL(circle));
      } else {
        setProfileImagePreview(null);
      }
    };
  
    // Change handlers
    const handleDescriptionChange = (e) => {
      setDescription(e.target.value);
      updateWordCount(e.target.value);
    };
  
    // ========== The "Update Post" Submission ==========
    const handleSubmit = async (event) => {
      event.preventDefault();
      setIsSubmitting(true);
      setShowErrors(true);
  
      // Clear any top-level error
      setGeneralError('');
  
      const form = event.target;
      const descriptionVal = form['description'].value.trim();
      const cityVal = form['city'].value.trim();
      const stateVal2 = form['state'].value.trim();
      const countryVal = form['country'].value.trim();
      const locationValue = form['location'].value;
      const linkVal = form['link'].value.trim();
      const instagramVal = form['instagram'].value.trim();
      const twitterVal = form['twitter'].value.trim();
      const tiktokVal = form['tiktok'].value.trim();
      const youtubeVal = form['youtube'].value.trim();
      const facebookVal = form['facebook'].value.trim();
      const userNameVal = form['userName']?.value.trim(); // optional
      const emailVal = form['email']?.value.trim();
  
      let hasErrors = false;
      const newFieldErrors = {};
  
      // strip empty fun-fact answers
      const iceBreakersClean = Object.fromEntries(
        Object.entries(iceBreakers).filter(([, v]) => v.trim())
      );
  
      // ~~~~~ Field-level checks ~~~~~
      if (!descriptionVal) {
        newFieldErrors.description = 'Description is required.';
        hasErrors = true;
      }
  
      if (goals.length === 0) {
        newFieldErrors.goals = 'Please select at least one goal.';
        hasErrors = true;
      }
  
      if (!locationValue) {
        newFieldErrors.location = 'Please select a location.';
        hasErrors = true;
      }
  
      if (!countryVal) {
        newFieldErrors.country = 'Country is required.';
        hasErrors = true;
      }
  
      /* ── NOW REQUIRED: profile image (existing OR new) ─────────── */
      const willHaveAvatar = profileImageBlob            // user just cropped a new one
                        || profileImagePreview;         // existing avatar kept
      if (!willHaveAvatar) {
        newFieldErrors.profileImage = 'A profile photo is required.';
        hasErrors = true;
      }
  
      if (wordCount > 80) {
        newFieldErrors.description = 'Description cannot exceed 80 words.';
        hasErrors = true;
      }
  
      // Validate link
      if (linkVal && !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/.test(linkVal)) {
        newFieldErrors.link = 'Please enter a valid URL';
        hasErrors = true;
      }
  
      // Validate email
      if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        newFieldErrors.email = 'Please enter a valid email address.';
        hasErrors = true;
      }
  
      setFieldErrors(newFieldErrors);
  
      if (hasErrors) {
        // Show the top-level error
        setGeneralError('One or more required fields are missing or invalid.');
        setIsSubmitting(false);
        return;
      }
  
      try {
        // =========== Profile Image Handling ===========
        let profileImageDownloadUrl  = post.profileImageUrl;      // avatar
        let fullProfileImageDownload = post.profileImageFullUrl;  // master square

        /* 1. upload (or clear) the full-frame square first */
        if (profileImageSquareBlob) {
          fullProfileImageDownload = await uploadImageToStorage(
            profileImageSquareBlob,
            `looksProfileImagesFull/${currentUser.uid}_${Date.now()}`
          );
        } else if (profileImageSquareBlob === null && profileImagePreview === null) {
          // user removed every image
          fullProfileImageDownload = null;
        }

        /* 2. then upload (or clear) the circular avatar */
        if (profileImageBlob) {
          profileImageDownloadUrl = await uploadImageToStorage(
            profileImageBlob,
            `looksProfileImagesAvatar/${currentUser.uid}_${Date.now()}`
          );
        } else if (profileImagePreview === null) {
          profileImageDownloadUrl = null;
        }
  
        // Save or remove from localStorage if you wish
        localStorage.setItem('userName', userNameVal || '');

        if (profileImageDownloadUrl) {
          localStorage.setItem('profileImageUrl', profileImageDownloadUrl);
        } else {
          localStorage.removeItem('profileImageUrl');
        }

        if (fullProfileImageDownload) {
          localStorage.setItem('profileImageFullUrl', fullProfileImageDownload);
        } else {
          localStorage.removeItem('profileImageFullUrl');
        }
  
        // Final post data
        const updatedPostData = {
          description: descriptionVal,
          city: cityVal,
          state: stateVal2,
          country: countryVal,
          location: userLocation,
          goals,
          link: linkVal,
          instagram: instagramVal,
          twitter: twitterVal,
          tiktok: tiktokVal,
          youtube: youtubeVal,
          facebook: facebookVal,
          email: emailVal || null,
          iceBreakers: iceBreakersClean,      // ✓ fun-facts
          profileImageUrl: profileImageDownloadUrl || null,
          profileImageFullUrl: fullProfileImageDownload || null,
          userName: userNameVal || '', // optional
          lastEdited: new Date().toISOString()
        };
  
        // Update in Firestore
        const updatedPost = await updatePostInDB('looksPosts', id, updatedPostData);
        if (!updatedPost) {
          throw new Error('Failed to update post');
        }
  
        // Prepare final updated post (we'll attach summary if we get it)
        const updatedPostWithSummary = { ...updatedPost };
  
        // === Attempt to update summary if description changed ===
        if (descriptionVal !== post.description) {
          try {
            const newSummary = await updatePostSummary(id, descriptionVal);
            if (newSummary) {
              updatedPostWithSummary.summary = newSummary;
            }
          } catch (summaryError) {
            // If summary generation fails, do not fail the entire update
            console.error('Error generating summary:', summaryError);
            setGeneralError('Unable to generate summary, but your post was updated successfully.');
          }
        }
  
        // Update local context
        updateSinglePost(updatedPostWithSummary);
  
        // All done, finalize
        setIsSubmitting(false);
        navigate(`/looksposts/${updatedPost.id}`);
      } catch (error) {
        console.error('Error updating post:', error);
        setIsSubmitting(false);
      }
    };
  
    // Close
    const handleClose = () => {
      navigate(-1);
    };
  
    // If loading user, show a spinner
    if (loading) {
      return (
        <div className="form-container" style={{ marginTop: '150px', color: 'black' }}>
          Loading user...
        </div>
      );
    }
  
    // If no post data loaded yet
    if (!post) {
      return (
        <div className="form-container" style={{ marginTop: '150px', color: 'black' }}>
          Loading...
        </div>
      );
    }
  
    // ~~~~~ Render ~~~~~
    return (
      <div
        className="form-container"
        style={{ marginTop: '150px', backgroundColor: 'white' }}
      >
        <CloseButton onClick={handleClose} />
    
        <div className="form-header">
          <h2 className="form-title" style={{ color: 'black' }}>
            Edit Post
          </h2>
        </div>
    
        <form className="post-form" onSubmit={handleSubmit}>
          {/* ====== Name ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }}>What's your name?</label>
            <input
              type="text"
              name="userName"
              className={`form-input ${
                showErrors && fieldErrors.userName ? 'error' : ''
              }`}
              placeholder="First Name, Nickname, Full Name — You Choose!"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
            {showErrors && fieldErrors.userName && (
              <p className="error-message">{fieldErrors.userName}</p>
            )}
          </div>
    
          {/* ====== Profile Image ====== */}
          <h3 style={{ color: 'black', marginBottom: '8px' }}>Profile Image</h3>
  
          <ProfileImageDropzone
            onImageUpload={handleProfileImageUpload}
            setIsImageProcessing={setProfileImageProcessing}
            setProgress={setProfileImageProgress}
            setUploadError={setProfileImageError}
            initialImage={profileImagePreview}
            onCropperOpen={setIsProfileCropperOpen}
          />
  
          {/* 🔹 runtime issues while uploading/cropping */}
          {profileImageError && (
            <p className="error-message">{profileImageError}</p>
          )}
  
          {/* 🔹 *validation* error shown after the user clicks Update */}
          {showErrors && fieldErrors.profileImage && (
            <p className="error-message">{fieldErrors.profileImage}</p>
          )}
  
          {profileImageProcessing && (
            <div className="progress-bar-container">
              <progress value={profileImageProgress} max="100" />
            </div>
          )}
  
          <p
            style={{
              marginTop: '16px',
              fontSize: '0.95rem',
              color: '#555',
              fontStyle: 'italic',
              width: '70%',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          >
            Add a Photo of You to Boost Post Views!
          </p>
    
                  {/* ====== Description ====== */}
        <div className="form-group">
          <label style={{ color: 'black' }}>
            Tell us a little about yourself
          </label>
          <textarea
            name="description"
            className={`form-input ${
              showErrors && fieldErrors.description ? 'error' : ''
            }`}
            rows="4"
            placeholder="Briefly introduce yourself. 80 words max."
            value={description}
            onChange={handleDescriptionChange}
          />
          <p
            className={`word-count ${
              wordCount > 80 ? 'word-count-exceeded' : ''
            }`}
          >
            {wordCount} / 80 words
          </p>
          {showErrors && fieldErrors.description && (
            <p className="error-message">{fieldErrors.description}</p>
          )}
        </div>
  
        {/* ====== Goals (updated list) ====== */}
        <div className="form-group goals-group">
          <label style={{ color: 'black' }}>Select Your Goal(s)</label>
          <div className="form-options">
            {sortedGoals.map((goal) => (
              <div key={goal}>
                <input
                  type="checkbox"
                  id={goal.toLowerCase().replace(/\s+/g, '-') }
                  name="goals"
                  value={goal}
                  checked={goals.includes(goal)}
                  onChange={(e) => {
                    const { value, checked } = e.target;
                    setGoals((prev) =>
                      checked
                        ? [...prev, value]
                        : prev.filter((g) => g !== value)
                    );
                  }}
                />
                <label
                  htmlFor={goal.toLowerCase().replace(/\s+/g, '-') }
                  style={{ fontSize: '0.95rem' }}
                >
                  {goal}
                </label>
              </div>
            ))}
          </div>
          {showErrors && fieldErrors.goals && (
            <p className="error-message">{fieldErrors.goals}</p>
          )}
        </div>
  
        {/* ====== Ice-Breaker Questions ====== */}
        <div className="form-group">
          <label style={{ color: 'black' }}>
            Fun Facts About You (Optional)
          </label>
          {ICE_BREAKER_QUESTIONS.map((q) => (
            <div key={q.id} style={{ marginBottom: '16px' }}>
              {q.type === 'text' && (
                <input
                  type="text"
                  className="form-input"
                  placeholder={q.label}
                  value={iceBreakers[q.id]}
                  onChange={(e) =>
                    setIceBreakers((prev) => ({
                      ...prev,
                      [q.id]: e.target.value
                    }))
                  }
                />
              )}
  
              {q.type === 'choice' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}
                >
                  <span style={{ fontSize: '1.05rem' }}>{q.label}</span>
                  {q.options.map((opt) => (
                    <label
                      key={opt}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={iceBreakers[q.id] === opt}
                        onChange={() =>
                          setIceBreakers((prev) => ({
                            ...prev,
                            [q.id]: prev[q.id] === opt ? '' : opt
                          }))
                        }
                      />
                      <span style={{ marginLeft: '4px' }}>{opt}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
  
        {/* ====== Link ====== */}
        <div className="form-group">
          <label style={{ color: 'black' }}>Add a URL Link (Optional)</label>
          <input
            type="text"
            name="link"
            className={`form-input ${ showErrors && fieldErrors.link ? 'error' : '' }`}
            placeholder="e.g., example.com"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          {showErrors && fieldErrors.link && (
            <p className="error-message">{fieldErrors.link}</p>
          )}
        </div>
  
        {/* ====== Email ====== */}
        <div className="form-group">
          <label style={{ color: 'black' }}>Add Your Contact Email (Optional)</label>
          <input
            type="email"
            name="email"
            className={`form-input ${
              showErrors && fieldErrors.email ? 'error' : ''
            }`}
            placeholder="e.g., your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {showErrors && fieldErrors.email && (
            <p className="error-message">{fieldErrors.email}</p>
          )}
        </div>
  
        {/* ====== Socials ====== */}
        <div className="form-group">
          <label style={{ color: 'black' }}>Share Your Socials (Optional)</label>
          <div className="social-inputs">
            <div className="social-input">
              <FaInstagram className="social-icon instagram" />
              <input
                type="text"
                name="instagram"
                className="form-input"
                placeholder="Instagram Username"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
              />
            </div>
  
            <div className="social-input">
              <FaTwitter className="social-icon twitter" />
              <input
                type="text"
                name="twitter"
                className="form-input"
                placeholder="Twitter/X Username"
                value={twitter}
                onChange={(e) => setTwitter(e.target.value)}
              />
            </div>
  
            <div className="social-input">
              <FaTiktok className="social-icon tiktok" />
              <input
                type="text"
                name="tiktok"
                className="form-input"
                placeholder="TikTok Username"
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
              />
            </div>
  
            <div className="social-input">
              <FaYoutube className="social-icon youtube" />
              <input
                type="text"
                name="youtube"
                className="form-input"
                placeholder="YouTube Channel Handle"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
              />
            </div>
  
            <div className="social-input">
              <FaFacebook className="social-icon facebook" />
              <input
                type="text"
                name="facebook"
                className="form-input"
                placeholder="Facebook Username"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
              />
            </div>
  
            {/* ——— SoundCloud on its own row ——— */}
            <div className="social-input">
              <FaSoundcloud className="social-icon soundcloud" />
              <input
                type="text"
                name="soundcloud"
                className="form-input"
                placeholder="SoundCloud Handle"
                value={soundcloud}
                onChange={(e) => setSoundcloud(e.target.value)}
              />
            </div>
          </div>
        </div>
  
        {/* ====== Location / Country / City / State ====== */}
        <div className="form-group location-group">
          <label style={{ color: 'black' }}>Where Are You Based?</label>
          <div className="form-options">
            {[
              'North America',
              'Europe',
              'Africa',
              'Middle East',
              'Asia',
              'Central America',
              'South America',
              'Australia'
            ].map((loc) => (
              <div key={loc}>
                <input
                  type="radio"
                  id={loc.toLowerCase().replace(' ', '')}
                  name="location"
                  value={loc}
                  checked={userLocation === loc}
                  onChange={(e) => setUserLocation(e.target.value)}
                />
                <label htmlFor={loc.toLowerCase().replace(' ', '')}>{loc}</label>
              </div>
            ))}
          </div>
          {showErrors && fieldErrors.location && (
            <p className="error-message">{fieldErrors.location}</p>
          )}
        </div>
  
        {/* Country */}
        <div
          className="form-group"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%'
          }}
        >
          <label style={{ color: 'black', minWidth: '80px', paddingTop: '6px' }}>
            Country
          </label>
          <input
            type="text"
            name="country"
            className={`form-input ${
              showErrors && fieldErrors.country ? 'error' : ''
            }`}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
        {showErrors && fieldErrors.country && (
          <p className="error-message">{fieldErrors.country}</p>
        )}
  
        {/* City */}
        <div
          className="form-group city-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%'
          }}
        >
          <label style={{ color: 'black', minWidth: '110px', paddingTop: '6px' }}>
            City&nbsp;(Optional)
          </label>
          <input
            type="text"
            name="city"
            className="form-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
        {showErrors && fieldErrors.city && (
          <p className="error-message">{fieldErrors.city}</p>
        )}
  
        {/* State */}
        <div
          className="form-group state-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%'
          }}
        >
          <label style={{ color: 'black', minWidth: '110px', paddingTop: '6px' }}>
            State&nbsp;(Optional)
          </label>
          <input
            type="text"
            name="state"
            className="form-input"
            value={stateVal}
            onChange={(e) => setStateVal(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
  
        {/* ====== Top-level error ====== */}
        {generalError && (
          <p
            className="error-message"
            style={{ textAlign: 'center', marginTop: '20px' }}
          >
            {generalError}
          </p>
        )}
  
        {/* ====== Action Buttons ====== */}
        {!isProfileCropperOpen && (
          <div
            className="form-buttons-container"
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}
          >
            <button
              type="button"
              className="form-button cancel-button"
              onClick={() => navigate(`/looksposts/${id}`)}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                flex: 1,
                maxWidth: '200px'
              }}
            >
              Cancel
            </button>
  
            <button
              type="submit"
              className="form-button"
              disabled={isSubmitting}
              style={{ flex: 1, maxWidth: '200px' }}
            >
              {isSubmitting ? 'Updating…' : 'Update Post'}
            </button>
          </div>
        )}
      </form>
    </div>
  );  
};

export default LooksEditPostForm;